// App auth: a redirect-based OpenID Connect implicit flow (response_type=
// id_token). No GSI library, no injected DOM/styles — so the app runs under a
// pure `style-src 'self'` (no Google origin, no hashes). We render our own button;
// clicking it is a top-level navigation to Google, which returns the id_token in
// the URL fragment. We store it in the SAME `ff_auth` sessionStorage shape the
// shared @lib/auth reads, so the shared db client's getIdToken() works unchanged.
//
// Requires the redirect URI (this page's origin+path) to be registered in the
// Google OAuth client's "Authorized redirect URIs".
import { getStoredAuth } from '@lib/auth';
import { config } from '@lib/config';

const NONCE_KEY = 'oidc_nonce';
const STATE_KEY = 'oidc_state';
const AUTH_KEY = 'ff_auth'; // shared shape: { idToken, email }

function randomToken(): string {
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);
	return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function redirectUri(): string {
	// Base page (no hash) — Google appends "#id_token=..." here on return.
	return location.origin + location.pathname;
}

export function isAuthenticated(): boolean {
	return getStoredAuth().isAuthenticated;
}

export function userEmail(): string | null {
	return getStoredAuth().email;
}

/** Clear the stored session and reload to the login screen. */
export function logout(): void {
	sessionStorage.removeItem(AUTH_KEY);
	sessionStorage.removeItem(NONCE_KEY);
	sessionStorage.removeItem(STATE_KEY);
	location.hash = '';
	location.reload();
}

export function login(): void {
	const nonce = randomToken();
	const state = randomToken();
	try {
		sessionStorage.setItem(NONCE_KEY, nonce);
		sessionStorage.setItem(STATE_KEY, state);
	} catch {
		// sessionStorage unavailable (lockdown/private mode): the redirect would
		// come back unverifiable, so fail here with an explanation instead.
		alert('Sign-in needs session storage, which this browser mode blocks.');
		return;
	}
	const params = new URLSearchParams({
		client_id: config.google.clientId,
		redirect_uri: redirectUri(),
		response_type: 'id_token',
		scope: 'openid email',
		nonce,
		state,
		prompt: 'select_account',
	});
	location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
}

function parseJwt(token: string): Record<string, unknown> {
	const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
	const json = decodeURIComponent(
		atob(b64)
			.split('')
			.map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
			.join('')
	);
	return JSON.parse(json);
}

/** If we've just returned from Google, capture the id_token (validating state +
 * nonce) and store it. Returns true if a callback was handled — the caller then
 * routes to '/'. */
export function handleRedirectCallback(): boolean {
	const raw = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash;
	if (!raw.includes('id_token=')) return false;

	const params = new URLSearchParams(raw);
	const idToken = params.get('id_token');
	const state = params.get('state');
	const expectedState = sessionStorage.getItem(STATE_KEY);
	const expectedNonce = sessionStorage.getItem(NONCE_KEY);
	sessionStorage.removeItem(STATE_KEY);
	sessionStorage.removeItem(NONCE_KEY);

	try {
		if (idToken && state && state === expectedState) {
			const claims = parseJwt(idToken);
			if (claims.nonce === expectedNonce && typeof claims.email === 'string') {
				sessionStorage.setItem(AUTH_KEY, JSON.stringify({ idToken, email: claims.email }));
			}
		}
	} catch {
		// fall through — leaves the user signed out
	}
	// replaceState (not a hash assignment) so the #id_token=... URL never
	// lingers in browser history.
	history.replaceState(null, '', location.pathname + location.search + '#/');
	return true;
}
